<template>
    <div>
        <div v-if="siteLoading">
            <kgr-loader />
        </div>
        <div class="kgr-alert kgr-alert--info kgr-row">
            <h3 class="kgr-headline kgr-headline--h3">
                {{ title }}
            </h3>
            <div
                class="kgr-text kgr-text--preserveLineBreaks"
                v-html="infoText"
            >
            </div>
            <div
                v-if="showAdditionalInfo2"
                class="mt1 additionalInfo"
            >
                <SphIcon
                    :icon="Info"
                    size="md"
                    class="text-gray-600"
                />
                <span class="kgr-text">{{ $t('Die Entfernung eines Kontos bei Qwist hat zur Folge, dass alle zugehörigen Transaktionen im Bankabgleich gelöscht werden.') }}</span>
            </div>
            <div
                v-if="showAdditionalInfo1"
                class="mt1 additionalInfo"
            >
                <SphIcon
                    :icon="Info"
                    size="md"
                    class="text-gray-600"
                />
                <span class="kgr-text kgr-text--bold">{{ $t('Deine Zugangsdaten bei Qwist sind 90 Tage gültig. Spätestens nach diesem Zeitraum musst du deinen Kontenzugriff aktualisieren.') }}</span>
            </div>
        </div>
        <div
            v-if="showRegShieldButton"
        >
            <button
                class="kgr-button kgr-button--accent"
                :disabled="finleapButtonDisabled"
                @click="getRegShieldToken"
            >
                {{ regShieldButtonText }}
            </button>
            <div
                v-if="finleapButtonDisabled"
                class="kgr-inputGroup__text"
            >
                {{ finleapButtonDisabledText }}
            </div>
        </div>
        <div
            v-else-if="showBankAccounts"
        >
            <div class="kgr-row">
                <button
                    class="kgr-button kgr-button--accent"
                    :disabled="finleapButtonDisabled"
                    @click="getRegShieldToken"
                >
                    {{ $t('Kontenzugriff aktualisieren') }}
                </button>
                <div
                    v-if="finleapButtonDisabled"
                    class="kgr-inputGroup__text"
                >
                    {{ finleapButtonDisabledText }}
                </div>
            </div>
            <div class="kgr-row kgr-grid kgr-grid--gutters kgr-grid--bigGutters">
                <div class="kgr-grid__cell">
                    <kgr-form-row
                        :label="$t('Start')"
                        :error="error"
                        field-name="start"
                    >
                        <kgr-date-picker
                            v-model="start"
                        />
                    </kgr-form-row>
                </div>
                <div class="kgr-grid__cell">
                    <kgr-form-row
                        :label="$t('Ende')"
                        :error="error"
                        field-name="end"
                    >
                        <kgr-date-picker
                            v-model="end"
                        />
                    </kgr-form-row>
                </div>
                <div class="kgr-grid__cell">
                    <kgr-form-row
                        :label="$t('Sortierung')"
                        :error="error"
                        field-name="sort"
                    >
                        <kgr-generic-select
                            v-model="defaultSort"
                            :options="sortOptions"
                        />
                    </kgr-form-row>
                </div>
                <div class="kgr-grid__cell kgr-grid__cell--bottom">
                    <kgr-checkbox
                        v-model="filter.excludeConnected"
                        :label="$t('zugewiesene Transaktionen ausblenden')"
                    />
                </div>
            </div>
            <div class="kgr-row">
                <kgr-form-row
                    :label="$t('Wähle hier das Konto für den Abgleich aus')"
                    :error="error"
                    field-name="bankAccount"
                >
                    <kgr-generic-select
                        v-model="bankAccount"
                        :options="bankAccounts"
                        display="displayText"
                    />
                </kgr-form-row>
            </div>
        </div>
        <hr class="kgr-separator--light" />
        <div
            v-if="showNoTransactions"
            class="kgr-alert kgr-alert--info kgr-row"
        >
            {{ $t('Keine Transaktionen vorhanden') }}
        </div>
        <div v-if="showTransactions">
            <kgr-paginator
                v-if="showPagination"
                v-model="pagination"
                :length="totalTransactionCount"
                :show-limit-options="false"
                show-pages
            />
            <div
                v-for="(transaction, transactionIndex) in transactions"
                :key="transaction.id"
                class="kgr-grid kgr-grid--columnGutters"
            >
                <div class="kgr-grid__cell">
                    <div
                        class="transaction"
                        @click="selectTransaction(transaction, transactionIndex)"
                    >
                        <div class="transaction-header">
                            <div class="transaction-info">
                                <i
                                    class="kgr-indicator"
                                    :class="{ 'is-success': isConnected(transaction) }"
                                    style="margin-right: 5px;"
                                ></i>
                                <h3 class="kgr-headline kgr-headline--h3">
                                    {{ transaction.name }}
                                </h3>
                            </div>
                            <h3
                                class="kgr-headline kgr-headline--h3"
                                :class="getAmountColor(transaction)"
                            >
                                {{ transaction.formattedMoney }}
                            </h3>
                        </div>
                        <div class="transaction-body">
                            <p>{{ transaction.purpose }}</p><br />
                            <span>{{ formatDate(transaction.bookedAt) }} | {{ transaction.bookingText }}</span>
                        </div>
                    </div>
                </div>
                <div
                    class="kgr-grid__cell"
                >
                    <div
                        ref="tmp"
                        class="action-container"
                    >
                        <table
                            v-if="transaction.invoices.length > 0"
                            class="kgr-table kgr-table--block kgr-table--striped kgr-row"
                        >
                            <thead>
                                <tr class="kgr-table__row">
                                    <th
                                        class="kgr-table__cell kgr-table__cell--headline"
                                        colspan="4"
                                    >
                                        {{ $t('Zugeordnete Rechnungen') }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(transactionInvoice, invoiceIndex) in transaction.invoices"
                                    :key="transactionInvoice.id"
                                    class="kgr-table__row"
                                >
                                    <td class="kgr-table__cell">
                                        <kgr-invoice-icon
                                            :invoice="transactionInvoice"
                                        />
                                    </td>
                                    <td
                                        class="kgr-table__cell"
                                        v-html="getInvoiceText(transactionInvoice)"
                                    >
                                    </td>
                                    <td
                                        class="kgr-table__cell"
                                    >
                                        <kgr-invoice-edit-buttons
                                            :value="transactionInvoice"
                                            :club-dunning-settings="clubDunningSettings"
                                            @invoice-reload="reloadInvoice(transactionInvoice)"
                                            @invoice-reload-with-polling="reloadInvoice(transactionInvoice)"
                                        />
                                        <button
                                            class="kgr-table__action"
                                            @click="deleteTransactionInvoice(transactionInvoice.id, transactionIndex, invoiceIndex)"
                                        >
                                            <kgr-icon
                                                icon-class="clear"
                                                small
                                                :title="$t('Zuordnung aufheben')"
                                            />
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div>
                            <kgr-text-input
                                v-model="query"
                                :label="$t('Rechnung suchen (Rechnungsempfänger, Rechnungsnummer)')"
                                @post-input="searchInvoices($event)"
                            />
                            <kgr-loader v-if="invoicesLoading" />
                            <table
                                v-if="showInvoices"
                                class="kgr-table kgr-table--block kgr-row"
                            >
                                <tbody>
                                    <tr
                                        v-for="invoice in invoices"
                                        :key="invoice.id"
                                        class="kgr-table__row"
                                    >
                                        <td class="kgr-table__cell">
                                            <kgr-invoice-icon
                                                :invoice="invoice"
                                            />
                                        </td>
                                        <td
                                            class="kgr-table__cell"
                                            v-html="getInvoiceText(invoice)"
                                        >
                                        </td>
                                        <td
                                            class="kgr-table__cell"
                                        >
                                            <button
                                                class="kgr-table__action"
                                                @click="selectInvoice(invoice); hasPaymentDialog() ? openPaymentDialog() : connectTransactionAndInvoice(transaction, invoice, transactionIndex)"
                                            >
                                                <kgr-icon
                                                    icon-class="add"
                                                    circle
                                                    small
                                                    :title="$t('Zuordnung herstellen')"
                                                />
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <table
                                v-else-if="showEmptyInvoiceResult"
                                class="kgr-table kgr-table--block kgr-row"
                            >
                                <tbody>
                                    <tr>
                                        <td colspan="3">
                                            {{ $t('Keine Rechnungen gefunden') }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            <kgr-paginator
                v-if="showPagination"
                v-model="pagination"
                :length="totalTransactionCount"
                :show-limit-options="false"
                show-pages
            />
        </div>
        <kgr-lightbox
            :headline="$t('Zahlungsbetrag definieren')"
            :value="showPaymentDialog"
            animated
            @post-input="cancelPaymentDialog()"
        >
            <div
                v-if="selectedInvoice && selectedInvoice.clubTransactionCount > 0"
                class="kgr-alert kgr-alert--info kgr-row"
            >
                <span
                    class="kgr-text kgr-text--bold"
                >
                    {{ $t('HINWEIS:') }}<br />
                    Diese Rechnung wurde bereits {{ selectedInvoice.clubTransactionCount }} Transaktion<template v-if="selectedInvoice.clubTransactionCount > 1">en</template> zugeordnet!
                </span>
                <br /><br />
                {{ $t('Der Betrag der Transaktion stimmt nicht mit dem Betrag der Rechnung überein. Hier kannst du die Höhe der Zahlung für die ausgewählte Rechnung anpassen.') }}
            </div>
            <kgr-text-input
                v-model="payment.amount"
                inline
                @enter="submitPaymentDialog()"
            />
            <kgr-generic-select
                v-model="payment.currency"
                inline
                disabled
                :options="currencies"
            />
            <template #footer>
                <button
                    class="kgr-button"
                    @click="cancelPaymentDialog();"
                >
                    {{ $t('cancel') }}
                </button>
                <button
                    class="kgr-button kgr-button--accent"
                    :disabled="paymentButtonDisabled"
                    @click="closePaymentDialog();"
                >
                    {{ $t('save') }}
                </button>
            </template>
        </kgr-lightbox>
    </div>
</template>

<script>
import { format, parse, startOfMonth } from '@legacy/js/date/date-fns.js';
import Checkbox from '../../../components/common/form/checkbox/Checkbox.vue';
import ClubDunningSettings from '../../../js/dto/ClubDunningSettings.js';
import ClubDunningSettingsRepository from '../../../js/repository/ClubDunningSettingsRepository.js';
import ClubInvoice from '../../../js/dto/ClubInvoice.js';
import ClubInvoicePaymentRepository from '../../../js/repository/ClubInvoicePaymentRepository.js';
import ClubInvoiceRepository from '../../../js/repository/ClubInvoiceRepository.js';
import ClubRepository from '../../../js/repository/ClubRepository.js';
import ClubTransactionInvoice from '../../../js/dto/ClubTransactionInvoice.js';
import ClubTransactionInvoiceRepository from '../../../js/repository/ClubTransactionInvoiceRepository.js';
import DatePicker from '../../../components/common/form/picker/DatePicker.vue';
import Error from '../../../js/model/Error.js';
import FinleapConnectRepository from '../../../js/repository/FinleapConnectRepository.js';
import FinleapConnectSession from '../../../js/dto/FinleapConnectSession.js';
import FormRow from '../../../components/common/form/FormRow.vue';
import GenericSelect from '../../../components/common/form/select/GenericSelect.vue';
import Helper from '../../../js/accounting/Helper.js';
import Icon from '../../../components/common/icon/Icon.vue';
import { Info } from 'lucide';
import InvoiceEditButtons from '../../../components/club/accounting/InvoiceEditButtons.vue';
import InvoiceIcon from '../../../components/club/accounting/InvoiceIcon.vue';
import Lightbox from '../../../components/common/modal/Lightbox.vue';
import Loading from '../../../components/common/Loading.vue';
import Paginator from '../../../components/common/Paginator.vue';
import { SphIcon } from '@/components/base/icon/index.ts';
import TextInput from '../../../components/common/form/text-input/TextInput.vue';
import axios from 'axios';

const MIN_SEARCH_PHRASE_LENGTH = 3;
const SORT_COLUMN_ID = 'id';
const SORT_COLUMN_AMOUNT = 'amount.amount.numerator';
const SORT_ASC = 'asc';
const SORT_DESC = 'desc';
const SORT_BOOKING_DATE_DESC = 'Buchungsdatum absteigend';
const SORT_BOOKING_DATE_ASC = 'Buchungsdatum aufsteigend';
const SORT_AMOUNT_DESC = 'Betrag absteigend';
const SORT_AMOUNT_ASC = 'Betrag aufsteigend';
const INVOICE_PAYMENT_CONTEXT = 'finleapConnect';
const PAYMENT_AMOUNT_REGEX = /^-?\d+([.,]\d{1,2})?$/;

export default {
    components: {
        'kgr-loader': Loading,
        'kgr-icon': Icon,
        'kgr-form-row': FormRow,
        'kgr-date-picker': DatePicker,
        'kgr-checkbox': Checkbox,
        'kgr-generic-select': GenericSelect,
        'kgr-invoice-icon': InvoiceIcon,
        'kgr-text-input': TextInput,
        'kgr-lightbox': Lightbox,
        'kgr-paginator': Paginator,
        'kgr-invoice-edit-buttons': InvoiceEditButtons,
        SphIcon,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            error: new Error(),
            clubDunningSettings: new ClubDunningSettings(),
            siteLoading: false,
            transactionsLoading: false,
            invoicesLoading: false,
            clubEmail: null,
            helper: new Helper(),
            session: new FinleapConnectSession(),
            start: format(startOfMonth(new Date()), 'dd.MM.yyyy'),
            end: format(new Date(), 'dd.MM.yyyy'),
            bankAccounts: [],
            bankAccount: null,
            transactions: [],
            selectedTransaction: null,
            selectedTransactionIndex: null,
            searchTimer: null,
            query: '',
            invoices: [],
            selectedInvoice: null,
            invoiceResult: false,
            filter: {
                excludeConnected: false,
            },
            pagination: {
                offset: 0,
                limit: 10,
            },
            totalTransactionCount: 0,
            pageCount: 0,
            sortOptions: [
                SORT_BOOKING_DATE_DESC,
                SORT_BOOKING_DATE_ASC,
                SORT_AMOUNT_DESC,
                SORT_AMOUNT_ASC,
            ],
            defaultSort: SORT_BOOKING_DATE_DESC,
            showPaymentDialog: false,
            payment: {
                amount: '',
                currency: 'EUR',
            },
            currencies: [
                'EUR',
                'USD',
            ],
            Info,
        };
    },
    computed: {
        title() {
            if (this.siteLoading) {
                return '';
            }
            return this.session.id === null || !this.session.refreshTokenValid
                ? this.$t('Qwist Registrierungsprozess')
                : this.$t('Kontenübersicht');
        },
        infoText() {
            if (this.siteLoading) {
                return '';
            }
            if (this.session.id === null) {
                return 'Die Qwist GmbH ist ein reguliertes Zahlungsinstitut nach dem Zahlungsdienstaufsichtsgesetz (ZAG) mit Standorten in Berlin, Hamburg, München und Madrid.<br/>'
                    + 'Die Qwist GmbH gehört zu Europas führenden Unternehmen im Bereich Finanzdienstleistungen.<br/><br/>'
                    + 'Über Qwist können Bankkonten in Clubity eingebunden werden. Die Zahlungseingänge auf den eingebundenen Bankkonten können mit den über Clubity gestellten Rechnungen verknüpft werden.<br/>'
                    + 'Dadurch können SEPA-Lastschriften, Überweisungen und Rücklastschriften als Historie an der Rechnung gespeichert werden. So kann jeder Zahlungsvorgang der Mitglieder schnell und übersichtlich erfasst werden.<br/><br/>'
                    + 'Im Zuge der Registrierung wirst Du auf die Seite von Qwist weitergeleitet und dort zur Eingabe deiner Bankzugangsdaten aufgefordert.<br/>'
                    + 'Diese Zugangsdaten können von Clubity nicht eingesehen werden. Sie werden dementsprechend von Clubity auch nicht gespeichert.<br/>'
                    + 'Nach erfolgreicher Registrierung wirst du auf diese Seite zurückgeleitet, hier stehen deine gewählten Bankkonten anschließend zur Auswahl bereit.<br/><br/>'
                    + 'Die Transaktionen deiner verbundenen Konten werden viermal täglich zwischen 8 Uhr und 18 Uhr automatisch aktualisiert.';
            } else if (!this.session.refreshTokenValid) {
                return this.$t('Du hast Qwist seit über 90 Tagen nicht verwendet. Bitte autorisiere diesen Service erneut.');
            }
            return 'Hier findest du eine Übersicht der Transaktionen deiner mit Qwist verbundenen Bankkonten.<br/>'
                + 'Die Transaktionen deiner verbundenen Konten werden viermal täglich zwischen 8 Uhr und 18 Uhr automatisch aktualisiert.<br/><br/>'
                + 'Eine Aktualisierung des Kontenzugriffs ist notwendig, wenn Du die Auswahl der Bank oder des Kontos ändern oder erweitern möchtest.<br/><br/>'
                + 'Für den Abgleich werden nur Bankkonten mit gültiger IBAN zur Auswahl bereitgestellt.';
        },
        showAdditionalInfo1() {
            return this.session.id !== null && this.session.refreshTokenValid;
        },
        showAdditionalInfo2() {
            return this.session.id !== null;
        },
        finleapButtonDisabled() {
            return this.clubEmail === null;
        },
        finleapButtonDisabledText() {
            return this.$t('Für die Nutzung des Bankabgleich muss unter Verein > Stammdaten > Kontaktdaten eine E-Mail-Adresse im Feld "E-Mail für Clubity" eingetragen werden.');
        },
        showRegShieldButton() {
            return !this.siteLoading && (this.session.id === null || !this.session.refreshTokenValid);
        },
        regShieldButtonText() {
            return this.session.id === null ? this.$t('Registrierung starten') : this.$t('Registrierung erneuern');
        },
        showBankAccounts() {
            return !this.siteLoading && this.session.id !== null;
        },
        selectedBankAccount() {
            return this.bankAccount !== undefined && this.bankAccount !== null;
        },
        showTransactions() {
            return !this.siteLoading && !this.transactionsLoading && this.selectedBankAccount && this.transactions.length > 0;
        },
        showPagination() {
            return this.showTransactions && (this.pageCount > 1 || this.pagination.offset > 0);
        },
        showNoTransactions() {
            return !this.siteLoading && !this.transactionsLoading && this.selectedBankAccount && this.transactions.length === 0;
        },
        showInvoices() {
            return this.showTransactions && !this.invoicesLoading && this.invoices.length > 0;
        },
        showEmptyInvoiceResult() {
            return this.invoiceResult && this.invoices.length === 0;
        },
        parsedPaymentAmount() {
            return Number(this.payment.amount.replace(',', '.'));
        },
        paymentButtonDisabled() {
            return !PAYMENT_AMOUNT_REGEX.test(this.payment.amount) || this.parsedPaymentAmount === 0;
        },
    },
    watch: {
        'session.id': function () {
            if (this.session.id !== null && this.session.refreshTokenValid) {
                this.getClubBankAccounts();
            }
        },
        start() {
            this.reloadFromFirstPage();
        },
        end() {
            this.reloadFromFirstPage();
        },
        defaultSort() {
            if (this.selectedBankAccount !== null) {
                this.reloadFromFirstPage();
            }
        },
        bankAccount() {
            this.reloadFromFirstPage();
        },
        'filter.excludeConnected': function () {
            this.reloadFromFirstPage();
        },
        query() {
            if (this.query === '') {
                this.invoices = [];
            }
        },
        'pagination.offset': function () {
            this.loadTransactions();
        },
    },
    created() {
        this.loadClub();
        this.getSession();
        this.loadClubDunningSettings();
        const state = this.parseParameter('state');
        const authCode = this.parseParameter('code');
        if (authCode !== null && state !== null) {
            this.createOrUpdateSession(state, authCode);
        } else if (authCode === null && state !== null) {
            this.redirect();
        }
    },
    methods: {
        reloadFromFirstPage() {
            if (this.pagination.offset === 0) {
                this.loadTransactions();
            } else {
                this.pagination.offset = 0;
            }
        },
        loadClub() {
            ClubRepository.getClub(this.clubId)
                .then((club) => this.clubEmail = club.email2)
                .catch(() => this.showErrorDialog());
        },
        getSession() {
            this.siteLoading = true;
            FinleapConnectRepository.getSessionByClubId(this.clubId)
                .then((response) => this.session = response)
                .catch(() => this.showErrorDialog())
                .finally(() => this.siteLoading = false);
        },
        getRegShieldToken() {
            this.siteLoading = true;
            FinleapConnectRepository.getRegShieldTokenByClubId(this.clubId)
                .then((response) => window.location = response.location)
                .catch((error) => {
                    let message = null;
                    if (error.response.status === 400) {
                        message = 'Für die Nutzung des Bankabgleich muss unter Verein > Stammdaten > Kontaktdaten eine E-Mail-Adresse im Feld "E-Mail für Clubity" eingetragen werden.';
                    }
                    error = null;
                    this.showErrorDialog(message);
                })
                .finally(() => this.siteLoading = false);
        },
        createOrUpdateSession(state, authCode) {
            if (this.session.id !== null) {
                return;
            }
            FinleapConnectRepository.updateSession(this.clubId, state, authCode)
                .then(() => this.redirect())
                .catch(() => this.showErrorDialog());
        },
        getClubBankAccounts() {
            this.siteLoading = true;
            FinleapConnectRepository.getBankAccountsByClubId(this.clubId)
                .then((response) => this.bankAccounts = response)
                .catch(() => this.showErrorDialog())
                .finally(() => this.siteLoading = false);
        },
        loadTransactions() {
            const parsedStart = parse(this.start, 'dd.MM.yyyy', new Date());
            const formattedStart = format(parsedStart, 'yyyy-MM-dd');
            const parsedEnd = parse(this.end, 'dd.MM.yyyy', new Date());
            const formattedEnd = format(parsedEnd, 'yyyy-MM-dd');
            if (!this.selectedBankAccount) {
                this.transactions = [];
                return;
            }
            if (formattedEnd < formattedStart) {
                this.$notify.warning('Das Enddatum darf nicht vor dem Startdatum liegen.');
                return;
            }
            this.transactionsLoading = true;
            FinleapConnectRepository.getCountAndTransactionsByBankAccountIdAndFilters(
                this.bankAccount.id,
                this.buildQueryString(false),
                this.buildQueryString(true),
            )
                .then((response) => {
                    this.totalTransactionCount = response[0];
                    this.transactions = response[1];
                    this.updatePaginationLength();
                })
                .catch(() => this.showErrorDialog())
                .finally(() => this.transactionsLoading = false);
        },
        buildQueryString(fullQuery) {
            const parsedStart = parse(this.start, 'dd.MM.yyyy', new Date());
            const formattedStart = format(parsedStart, 'yyyy-MM-dd');
            const parsedEnd = parse(this.end, 'dd.MM.yyyy', new Date());
            const formattedEnd = format(parsedEnd, 'yyyy-MM-dd');
            const query = [
                `dateRange=${formattedStart}:${formattedEnd}`,
                `excludeConnected=${this.filter.excludeConnected}`,
            ];
            if (fullQuery) {
                query.push(...[
                    `limit=${this.pagination.limit}`,
                    `offset=${this.pagination.offset}`,
                    `col=${this.mapOrderColumn()}`,
                    `order=${this.mapOrder()}`,
                ]);
            }
            return `?${query.join('&')}`;
        },
        mapOrderColumn() {
            if (SORT_BOOKING_DATE_DESC === this.defaultSort || SORT_BOOKING_DATE_ASC === this.defaultSort) {
                return SORT_COLUMN_ID;
            }
            return SORT_COLUMN_AMOUNT;
        },
        mapOrder() {
            if (SORT_BOOKING_DATE_ASC === this.defaultSort || SORT_AMOUNT_ASC === this.defaultSort) {
                return SORT_ASC;
            }
            return SORT_DESC;
        },
        selectTransaction(transaction, index) {
            this.selectedTransaction = transaction;
            this.selectedTransactionIndex = index;
            this.query = '';
            this.invoices = [];
            this.$refs.tmp.forEach((element) => element.style.display = 'none');
            this.$refs.tmp[index].style.display = 'block';
            this.invoiceResult = false;
        },
        selectInvoice(invoice) {
            this.selectedInvoice = invoice;
        },
        searchInvoices(event) {
            const excluded = ['Escape', 'ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft', 'Enter', ' '];
            if (excluded.includes(event.key)) {
                return;
            }
            if (this.query.trim().length < MIN_SEARCH_PHRASE_LENGTH) {
                return;
            }
            clearTimeout(this.searchTimer);
            const strokesPerMinute = 200;
            this.searchTimer = setTimeout(() => {
                this.sendSearchInvoiceRequest();
            }, 1000 / (strokesPerMinute / 60));
        },
        sendSearchInvoiceRequest() {
            this.invoicesLoading = true;
            this.invoiceResult = false;
            const url = `/club/clubs/${this.clubId}/invoices/search?query=${encodeURIComponent(this.query.trim())}&limit=20&offset=0`;
            axios.get(url)
                .then((response) => {
                    this.invoicesLoading = false;
                    this.invoices = response.data.data.map((invoice) => {
                        return ClubTransactionInvoice.fromData(invoice);
                    }).filter((invoice) => !this.selectedTransaction.invoices.some((transactionInvoice) => transactionInvoice.invoiceId === invoice.id));
                    this.invoiceResult = true;
                })
                .catch(() => this.showErrorDialog());
        },
        getInvoiceText(invoice) {
            return `${invoice.number}, ${invoice.invoiceRecipient}, ${
                this.helper.formatDate(invoice.createdAt)}, ${
                invoice.amount}${this.getMoney(invoice.dunningAmount)
            }${invoice.chargeback && invoice.chargeback.amount ? this.getMoney(invoice.chargeback.amount) : ''
            }${invoice.canceled ? ' (storniert)' : ''}`;
        },
        getMoney(money) {
            if (!money) {
                return '';
            }
            const amount = (money.amount.numerator / money.amount.denominator).toFixed(2);
            return ` <span style="color: red;">(${amount} €)</span>`;
        },
        showErrorDialog(hintMessage) {
            if (hintMessage !== null) {
                this.$notify.warning(hintMessage);
            } else {
                this.$notify.error('Es ist ein interner Fehler aufgetreten.');
            }
            this.siteLoading = false;
            this.transactionsLoading = false;
            this.invoicesLoading = false;
        },
        connectTransactionAndInvoice() {
            if (this.selectedInvoice.status.status === ClubInvoice.STATUS_PAID && this.selectedInvoice.clubTransactionCount === 0) {
                this.createTransactionInvoice();
            } else {
                // add transaction amount as a payment
                const date = new Date(this.selectedTransaction.bookedAt.toString());
                const data = {
                    money: {
                        amount: { numerator: this.selectedTransaction.amount.numerator, denominator: this.selectedTransaction.amount.denominator },
                        currency: this.selectedInvoice.currency,
                    },
                    appliedAt: { day: date.getDate(), month: date.getMonth() + 1, year: date.getFullYear() },
                    context: INVOICE_PAYMENT_CONTEXT,
                };
                if (this.payment.amount !== '' && this.payment.currency !== null) {
                    data.money = {
                        amount: { numerator: Math.round(this.parsedPaymentAmount * 100), denominator: 100 },
                        currency: this.payment.currency,
                    };
                }
                // firstly, create invoice payment due to transaction date and amount
                ClubInvoicePaymentRepository.createByClubInvoiceId(this.selectedInvoice.id, data)
                    .then(() => this.createTransactionInvoice())
                    .catch(() => this.$notify.error('saveChanges.error'));
            }
        },
        hasPaymentDialog() {
            if (this.selectedInvoice.status.status === ClubInvoice.STATUS_PAID) {
                return this.selectedInvoice.clubTransactionCount !== 0;
            }
            return this.getTransactionAmount() !== this.getInvoiceAmount();
        },
        openPaymentDialog() {
            this.showPaymentDialog = true;
            let paymentAmount = 0;
            paymentAmount += this.getInvoiceAmount();
            if (this.selectedInvoice.dunningAmount) {
                paymentAmount += this.selectedInvoice.dunningAmount.amount.numerator / this.selectedInvoice.dunningAmount.amount.denominator;
            }
            if (this.selectedInvoice.chargeback && this.selectedInvoice.chargeback.amount) {
                paymentAmount += this.selectedInvoice.chargeback.amount.amount.numerator / this.selectedInvoice.chargeback.amount.amount.denominator;
            }
            this.payment.amount = paymentAmount.toFixed(2).replace('.', ',');
            this.payment.currency = this.selectedInvoice.currency;
        },
        closePaymentDialog() {
            this.showPaymentDialog = false;
            this.connectTransactionAndInvoice();
            this.resetPayment();
        },
        submitPaymentDialog() {
            if (this.paymentButtonDisabled) {
                return;
            }
            this.closePaymentDialog();
        },
        cancelPaymentDialog() {
            this.showPaymentDialog = false;
            this.resetPayment();
        },
        resetPayment() {
            this.payment.amount = '';
            this.payment.currency = 'EUR';
        },
        getTransactionAmount() {
            return this.selectedTransaction.amount.numerator / this.selectedTransaction.amount.denominator;
        },
        getInvoiceAmount() {
            return this.selectedInvoice.gross.data.numerator / this.selectedInvoice.gross.data.denominator;
        },
        createTransactionInvoice() {
            ClubTransactionInvoiceRepository.create(this.selectedTransaction.id, this.selectedInvoice.id)
                .then((xIdentifier) => {
                    const transactionInvoice = ClubTransactionInvoice.fromInvoice(xIdentifier, this.selectedInvoice);
                    this.transactions[this.selectedTransactionIndex].invoices.push(transactionInvoice);
                    this.invoices = this.invoices.filter((foundInvoice) => foundInvoice.id !== this.selectedInvoice.id);
                    this.reloadInvoice(transactionInvoice);
                })
                .catch(() => this.showErrorDialog());
        },
        deleteTransactionInvoice(transactionInvoiceId, transactionIndex, invoiceIndex) {
            showConfirm(this.$t('confirmInformal'), this.$t('Willst Du die Zuordnung zur Rechnung  wirklich aufheben und diesen Zahlungseintrag für die Rechnung löschen?'), () => {
                ClubTransactionInvoiceRepository.deleteById(transactionInvoiceId)
                    .then(() => this.transactions[transactionIndex].invoices.splice(invoiceIndex, 1))
                    .catch(() => this.showErrorDialog());
            });
        },
        isConnected(transaction) {
            return transaction.invoices.length > 0;
        },
        parseParameter(parameterName) {
            const value = window.location.search
                .substr(1)
                .split('&')
                .map((item) => item.split('='))
                .filter((item) => item[0] === parameterName)
                .map((item) => item[1])[0];
            return value === undefined ? null : value;
        },
        redirect() {
            window.location = `/backend/clubs/${this.clubId}/accounting/bank-balancing`;
        },
        getAmountColor(transaction) {
            return transaction.outgoingPayment ? 'negativeAmount' : '';
        },
        updatePaginationLength() {
            this.pageCount = Math.ceil(this.totalTransactionCount / this.pagination.limit);
        },
        reloadInvoice(transactionInvoice) {
            ClubInvoiceRepository.find(transactionInvoice.invoiceId)
                .then((clubInvoice) => {
                    transactionInvoice.status = clubInvoice.status;
                    transactionInvoice.payments = clubInvoice.payments;

                    // workaround to hide tooltips that are left over from deleted elements
                    setTimeout(() => {
                        window?.hideObsoleteTooltips?.();
                    }, 100);
                });
        },
        formatDate(date) {
            return this.helper.formatDate(date);
        },
        loadClubDunningSettings() {
            ClubDunningSettingsRepository.findByClubId(this.clubId)
                .then((response) => this.clubDunningSettings = response)
                .catch(() => this.showErrorDialog());
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss">
    .additionalInfo {
    display: flex;
    align-items: center;
    gap: 8px;
}
.transaction {
    border: 1px solid #ccc;
    padding: 16px;
    cursor: pointer;
}
.transaction-header {
    display: flex;
    justify-content: space-between;
}
.transaction-info {
    display: flex;
    align-items: center;
}
.transaction-body {
    margin-top: 15px;
}
.action-container {
    display: none;
}
.right {
    float: right;
}
.secondCol {
    display: none;
}
.dotNotConnected {
    height: 15px;
    width: 15px;
    background-color: #bbb;
    border-radius: 50%;
    display: inline-block;
}
.dotConnected {
    height: 15px;
    width: 15px;
    background-color: #00ff00;
    border-radius: 50%;
    display: inline-block;
}
.negativeAmount {
    color: red;
}
</style>
